import React, { useState, useEffect } from 'react';
import { pathConstants } from '../../constants';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { userActions, studentActions, classActions } from '../../redux/actions';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input } from 'reactstrap';
import styled, { keyframes } from 'styled-components';
import { FiUser, FiShield, FiBook, FiAlertTriangle, FiTrash2 } from 'react-icons/fi';
import {
  UsersContainer,
  StyledButton,
  StyledTableHeader,
  HeaderButtonsContainer,
  FilterInput,
  TableContainer,
  StyledTable,
  RoleBadge,
  WarningButton,
  DangerButton,
  SuccessButton,
  SecondaryButton,
  UserCountContainer,
} from './UserManagePage.styled';

// ── Delete confirm modal styles ───────────────────────────────────────────────
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const slideUp = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}`;
const Overlay = styled.div`position:fixed;inset:0;background:rgba(0,0,0,0.5);display:flex;align-items:center;justify-content:center;z-index:9999;padding:20px;animation:${fadeIn} 0.2s ease;`;
const ModalCard = styled.div`background:#fff;border-radius:18px;max-width:460px;width:100%;box-shadow:0 20px 60px rgba(0,0,0,0.25);animation:${slideUp} 0.25s ease;overflow:hidden;`;
const MHeader = styled.div`background:linear-gradient(135deg,#ef4444,#dc2626);padding:20px 24px;display:flex;align-items:center;gap:12px;color:#fff;h3{margin:0;font-size:18px;font-weight:800;}p{margin:4px 0 0;font-size:13px;opacity:0.9;}`;
const MBody = styled.div`padding:20px 24px;`;
const MFooter = styled.div`padding:16px 24px;display:flex;gap:10px;justify-content:flex-end;border-top:1px solid #f3f4f6;`;
const SectionTitle = styled.div`font-size:12px;font-weight:700;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:10px;`;
const ItemRow = styled.div<{info?:boolean}>`display:flex;align-items:center;justify-content:space-between;padding:8px 12px;border-radius:8px;margin-bottom:6px;font-size:13px;background:${p=>p.info?'#eff6ff':'#fef2f2'};border:1px solid ${p=>p.info?'#bfdbfe':'#fecaca'};color:${p=>p.info?'#1e40af':'#991b1b'};`;
const CancelBtn = styled.button`padding:10px 20px;border-radius:10px;border:1.5px solid #e5e7eb;background:#fff;color:#374151;font-weight:600;font-size:13px;cursor:pointer;&:hover{background:#f9fafb;}`;
const DeleteBtn = styled.button`padding:10px 24px;border-radius:10px;border:none;background:linear-gradient(135deg,#ef4444,#dc2626);color:#fff;font-weight:700;font-size:13px;cursor:pointer;display:flex;align-items:center;gap:6px;&:hover{opacity:0.9;}&:disabled{opacity:0.5;cursor:not-allowed;}`;
const Spinner2 = styled.div`width:16px;height:16px;border:2px solid rgba(255,255,255,0.4);border-top-color:#fff;border-radius:50%;animation:spin2 0.6s linear infinite;@keyframes spin2{to{transform:rotate(360deg);}}`;

const UserManagePage: React.FC = (): JSX.Element => {
  const dispatch = useAppDispatch();

  const [userModalOpen, setUserModalOpen] = useState(false);
  const [changePasswordModalOpen, setChangePasswordModalOpen] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);
  const [deletePreview, setDeletePreview] = useState<any | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [userToChangePassword, setUserToChangePassword] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState<number | null>(null);
  const [userNameError, setUserNameError] = useState('');

  const [formData, setFormData] = useState({
    email: '',
    userName: '',
    password: '',
    passwordConfirmation: '',
    role: 2,
    id: null as number | null,
  });

  const [changePasswordData, setChangePasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const users = useAppSelector((state: any) => state.users.items || []);
  const userInfo = useAppSelector((state: any) => state.authentication.userInfo);
  const students = useAppSelector((state: any) => state.students.studentsList || []);
  const classes = useAppSelector((state: any) => state.classes?.classesList || []);

  useEffect(() => {
    dispatch(userActions.getAllTeachers());
    dispatch(studentActions.getAll());
    dispatch((classActions.getAll as any)());
  }, [dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Check for duplicate userName when field changes
    if (name === 'userName') {
      checkUserNameDuplicate(value);
    }
  };

  const checkUserNameDuplicate = (newUserName: string) => {
    // Only check if userName has a value
    if (!newUserName.trim()) {
      setUserNameError('');
      return;
    }

    // When editing, check if userName is different from original
    if (formData.id) {
      const originalUser = users.find((u: any) => u.id === formData.id);
      if (originalUser && originalUser.userName === newUserName) {
        // Same as original, no error
        setUserNameError('');
        return;
      }
    }

    // Check if userName already exists in other users
    const isDuplicate = users.some((u: any) => 
      u.userName === newUserName && u.id !== formData.id
    );

    if (isDuplicate) {
      setUserNameError('Tên đăng nhập đã tồn tại. Vui lòng chọn tên đăng nhập khác.');
    } else {
      setUserNameError('');
    }
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, role: Number(e.target.value) });
  };

  const handleCreateUser = () => {
    if (formData.password !== formData.passwordConfirmation) {
      alert('Password and Password Confirmation do not match!');
      return;
    }
    const { passwordConfirmation, ...userData } = formData;
    dispatch(userActions.createUser(userData));
    setTimeout(() => {
      dispatch(userActions.getAllTeachers());
    }, 300);
    resetForm();
  };

  const handleUpdateUser = () => {
    const { passwordConfirmation, password, ...userData } = formData;
    // Only include password if it was changed
    const updateData = password ? { ...userData, password } : userData;
    dispatch(userActions.updateUser(updateData));
    setTimeout(() => {
      dispatch(userActions.getAllTeachers());
    }, 300);
    resetForm();
  };

  const handleClickDeleteUser = async (user: any) => {
    setUserToDelete(user);
    setPreviewLoading(true);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/${user.id}/delete-preview`,
        { headers: { Authorization: `Bearer ${token}`, 'ngrok-skip-browser-warning': '69420' } },
      );
      const data = await res.json();
      setDeletePreview(data);
    } catch {
      // Fallback to simple confirm
      if (window.confirm(`Xoá user "${user.userName}"?`)) {
        dispatch(userActions.deleteUser(user.id));
        setTimeout(() => dispatch(userActions.getAllTeachers()), 300);
      }
      setUserToDelete(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await dispatch(userActions.deleteUser(userToDelete.id) as any);
      setTimeout(() => dispatch(userActions.getAllTeachers()), 300);
    } finally {
      setDeleting(false);
      setDeletePreview(null);
      setUserToDelete(null);
    }
  };

  const handleCancelDelete = () => { setDeletePreview(null); setUserToDelete(null); };

  const handleOpenChangePasswordModal = (user: any) => {
    setUserToChangePassword(user);
    setChangePasswordData({ newPassword: '', confirmPassword: '' });
    setChangePasswordModalOpen(true);
  };

  const handleChangePassword = () => {
    if (changePasswordData.newPassword !== changePasswordData.confirmPassword) {
      alert('Passwords do not match!');
      return;
    }
    if (!changePasswordData.newPassword || changePasswordData.newPassword.length < 6) {
      alert('Password must be at least 6 characters!');
      return;
    }
    dispatch(userActions.changeUserPassword(userToChangePassword.id, changePasswordData.newPassword));
    setTimeout(() => {
      dispatch(userActions.getAllTeachers());
    }, 300);
    setChangePasswordModalOpen(false);
    setUserToChangePassword(null);
  };

  const handleEditUser = (user: any) => {
    setFormData({
      email: user.email,
      userName: user.userName,
      password: '',
      passwordConfirmation: '',
      role: user.role,
      id: user.id,
    });
    setUserModalOpen(true);
    window.scrollTo(0, 0);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      userName: '',
      password: '',
      passwordConfirmation: '',
      role: 2,
      id: null,
    });
    setUserNameError('');
    setUserModalOpen(false);
  };

  const isDisabled = !formData.email || !formData.userName ||
    (!formData.id && !formData.password) ||
    (!formData.id && formData.password !== formData.passwordConfirmation) ||
    userNameError !== '';

  const getRoleName = (role: number) => {
    switch (role) {
      case 0: return 'Admin';
      case 1: return 'Teacher';
      case 2: return 'Student';
      default: return 'Unknown';
    }
  };

  const getRoleIcon = (role: number) => {
    switch (role) {
      case 0: return <FiShield size={16} />;
      case 1: return <FiBook size={16} />;
      case 2: return <FiUser size={16} />;
      default: return <FiUser size={16} />;
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Count users by role
  const totalAdmins = users.filter((u: any) => u.role === 0).length;
  const totalTeachers = users.filter((u: any) => u.role === 1).length;
  const totalStudents = users.filter((u: any) => u.role === 2).length;

  // Helper function to get student info by studentId
  const getStudentInfo = (studentId: number | null) => {
    if (!studentId) return null;
    const student = students.find((s: any) => s.id === studentId);
    if (!student) return null;
    const studentClass = classes.find((c: any) => c.id === student.classId);
    return { ...student, class: studentClass };
  };

  // Filter users
  const filteredUsers = users.filter((user: any) => {
    // Filter by role
    if (selectedRoleFilter !== null && user.role !== selectedRoleFilter) return false;

    // Filter by search term
    const searchStr = searchTerm.toLowerCase();
    const userName = user.userName ? user.userName.toLowerCase() : '';
    const email = user.email ? user.email.toLowerCase() : '';
    const roleStr = getRoleName(user.role).toLowerCase();

    // If user is a student, also search by student name and id
    let studentSearchMatch = false;
    if (user.role === 2 && user.studentId) {
      const student = getStudentInfo(user.studentId);
      if (student) {
        const studentName = student.name ? student.name.toLowerCase() : '';
        const studentId = student.id ? student.id.toString() : '';
        studentSearchMatch = studentName.includes(searchStr) || studentId.includes(searchStr);
      }
    }

    return (
      user.id.toString().includes(searchStr) ||
      userName.includes(searchStr) ||
      email.includes(searchStr) ||
      roleStr.includes(searchStr) ||
      studentSearchMatch
    );
  });

  return (
    <UsersContainer>
      <HeaderButtonsContainer>
        <div className="buttons-and-search">
          {userInfo?.role === 0 && (
            <StyledButton
              onClick={() => setUserModalOpen(true)}
              disabled={userModalOpen}
            >
              Create New User
            </StyledButton>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FilterInput
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e: any) => setSearchTerm(e.target.value)}
            />

            {userInfo?.role === 0 && (
              <UserCountContainer>
                <span
                  className="count-item admin"
                  onClick={() => setSelectedRoleFilter(selectedRoleFilter === 0 ? null : 0)}
                  style={{ cursor: 'pointer', fontWeight: selectedRoleFilter === 0 ? 'bold' : 'normal' }}
                >
                  <FiShield size={16} />
                  <span>Admin: {totalAdmins}</span>
                </span>
                <span
                  className="count-item teacher"
                  onClick={() => setSelectedRoleFilter(selectedRoleFilter === 1 ? null : 1)}
                  style={{ cursor: 'pointer', fontWeight: selectedRoleFilter === 1 ? 'bold' : 'normal' }}
                >
                  <FiBook size={16} />
                  <span>Teacher: {totalTeachers}</span>
                </span>
                <span
                  className="count-item student"
                  onClick={() => setSelectedRoleFilter(selectedRoleFilter === 2 ? null : 2)}
                  style={{ cursor: 'pointer', fontWeight: selectedRoleFilter === 2 ? 'bold' : 'normal' }}
                >
                  <FiUser size={16} />
                  <span>Student: {totalStudents}</span>
                </span>
              </UserCountContainer>
            )}
          </div>
        </div>
      </HeaderButtonsContainer>

      {/* User Modal */}
      {userInfo?.role === 0 && (
        <Modal isOpen={userModalOpen} toggle={() => { setUserModalOpen(false); setUserNameError(''); }} centered size="lg">
          <ModalHeader toggle={() => { setUserModalOpen(false); resetForm(); }}>
            {formData.id ? 'Edit User' : 'Create New User'}
          </ModalHeader>
          <ModalBody>
            <Form>
              <FormGroup>
                <Label for="email">
                  Email<span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="user@example.com"
                />
              </FormGroup>
              <FormGroup>
                <Label for="userName">
                  User Name<span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="text"
                  name="userName"
                  id="userName"
                  value={formData.userName}
                  onChange={handleInputChange}
                  placeholder="username"
                  style={{ borderColor: userNameError ? '#dc3545' : '' }}
                />
                {userNameError && (
                  <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                    {userNameError}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label for="password">
                  Password{!formData.id && <span style={{ color: 'red' }}>*</span>}
                  {formData.id && <span style={{ fontSize: '12px', color: '#666' }}> (Leave blank to keep current password)</span>}
                </Label>
                <Input
                  type="password"
                  name="password"
                  id="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="••••••••"
                />
              </FormGroup>
              {!formData.id && (
                <FormGroup>
                  <Label for="passwordConfirmation">
                    Password Confirmation<span style={{ color: 'red' }}>*</span>
                  </Label>
                  <Input
                    type="password"
                    name="passwordConfirmation"
                    id="passwordConfirmation"
                    value={formData.passwordConfirmation}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                  />
                  {formData.password !== formData.passwordConfirmation && formData.passwordConfirmation && (
                    <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                      Passwords do not match
                    </div>
                  )}
                </FormGroup>
              )}
              <FormGroup>
                <Label for="role">
                  Role<span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="select"
                  name="role"
                  id="role"
                  value={formData.role}
                  onChange={handleRoleChange}
                >
                  <option value={0}>Admin (0)</option>
                  <option value={1}>Teacher (1)</option>
                  <option value={2}>Student (2)</option>
                </Input>
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter>
            <SuccessButton
              onClick={() => {
                if (formData.id) handleUpdateUser();
                else handleCreateUser();
              }}
              disabled={isDisabled}
            >
              {formData.id ? 'Update User' : 'Create User'}
            </SuccessButton>
            <SecondaryButton
              onClick={() => {
                resetForm();
              }}
              style={{ marginLeft: '10px' }}
            >
              Cancel
            </SecondaryButton>
          </ModalFooter>
        </Modal>
      )}

      {/* Change Password Modal */}
      {userInfo?.role === 0 && (
        <Modal isOpen={changePasswordModalOpen} toggle={() => setChangePasswordModalOpen(false)} centered>
          <ModalHeader toggle={() => setChangePasswordModalOpen(false)}>
            Change Password for {userToChangePassword?.userName}
          </ModalHeader>
          <ModalBody>
            <Form>
              <FormGroup>
                <Label for="newPassword">
                  New Password<span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="password"
                  name="newPassword"
                  id="newPassword"
                  value={changePasswordData.newPassword}
                  onChange={(e: any) => setChangePasswordData({ ...changePasswordData, newPassword: e.target.value })}
                  placeholder="••••••••"
                />
              </FormGroup>
              <FormGroup>
                <Label for="confirmPassword">
                  Confirm Password<span style={{ color: 'red' }}>*</span>
                </Label>
                <Input
                  type="password"
                  name="confirmPassword"
                  id="confirmPassword"
                  value={changePasswordData.confirmPassword}
                  onChange={(e: any) => setChangePasswordData({ ...changePasswordData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                />
                {changePasswordData.newPassword !== changePasswordData.confirmPassword && changePasswordData.confirmPassword && (
                  <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                    Passwords do not match
                  </div>
                )}
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter>
            <SuccessButton
              onClick={handleChangePassword}
              disabled={!changePasswordData.newPassword || !changePasswordData.confirmPassword}
            >
              Change Password
            </SuccessButton>
            <SecondaryButton
              onClick={() => {
                setChangePasswordModalOpen(false);
                setUserToChangePassword(null);
              }}
              style={{ marginLeft: '10px' }}
            >
              Cancel
            </SecondaryButton>
          </ModalFooter>
        </Modal>
      )}      {/* Filter header */}
      {selectedRoleFilter !== null && (
        <div
          style={{
            textAlign: 'center',
            fontWeight: 700,
            textTransform: 'uppercase',
            margin: '12px 0',
            fontSize: 14,
          }}
        >
          {getRoleName(selectedRoleFilter)} USERS
        </div>
      )}

      <TableContainer>
        <StyledTable striped>
          <thead>
            <tr>
              <StyledTableHeader>ID</StyledTableHeader>
              <StyledTableHeader>User Name</StyledTableHeader>
              <StyledTableHeader>Email</StyledTableHeader>
              <StyledTableHeader>Role</StyledTableHeader>
              <StyledTableHeader>Created At</StyledTableHeader>
              {userInfo?.role === 0 && <StyledTableHeader>Actions</StyledTableHeader>}
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user: any) => (
              <tr key={user.id}>
                <td>{user.id}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {getRoleIcon(user.role)}
                    <div>
                      <div>{user.userName}</div>
                      {user.role === 2 && user.studentId && getStudentInfo(user.studentId) && (
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                          ({getStudentInfo(user.studentId)?.id} - {getStudentInfo(user.studentId)?.name} - {getStudentInfo(user.studentId)?.class?.name || 'N/A'})
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>
                  <RoleBadge role={user.role}>{getRoleName(user.role)}</RoleBadge>
                </td>
                <td>{formatDate(user.createdAt)}</td>
                {userInfo?.role === 0 && (
                  <td>
                    <WarningButton onClick={() => handleEditUser(user)}>Edit</WarningButton>{' '}
                    <StyledButton 
                      style={{ padding: '8px 16px', fontSize: '14px', marginRight: '8px' }}
                      onClick={() => handleOpenChangePasswordModal(user)}
                    >
                      Change Password
                    </StyledButton>
                    <DangerButton
                      onClick={() => handleClickDeleteUser(user)}
                      disabled={previewLoading && userToDelete?.id === user.id}
                    >
                      {previewLoading && userToDelete?.id === user.id ? '...' : 'Delete'}
                    </DangerButton>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </TableContainer>

      {userInfo?.role === 0 && (
        <p style={{ marginTop: '16px', fontWeight: 600 }}>
          Total: {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Delete Confirmation Popup */}
      {deletePreview && userToDelete && (
        <Overlay onClick={e => { if (e.target === e.currentTarget) handleCancelDelete(); }}>
          <ModalCard>
            <MHeader>
              <FiAlertTriangle size={26} />
              <div>
                <h3>Xoá user "{deletePreview.userName}"?</h3>
                <p>Hành động này không thể hoàn tác.</p>
              </div>
            </MHeader>
            <MBody>
              <SectionTitle>📋 Ảnh hưởng khi xoá</SectionTitle>

              {deletePreview.role === 1 && (
                <>
                  <div style={{ fontSize:12, color:'#6b7280', marginBottom:10, lineHeight:1.6 }}>
                    Các dữ liệu sau sẽ được <strong style={{color:'#1d4ed8'}}>gán lại cho admin ({userInfo?.userName})</strong> thay vì bị xoá, để đảm bảo lịch sử không bị mất:
                  </div>

                  {deletePreview.counts?.classesAsTeacher > 0 ? (
                    <ItemRow info>
                      <span>🏫 Lớp đang phụ trách (chuyển giáo viên → admin)</span>
                      <strong>{deletePreview.counts.classesAsTeacher} lớp</strong>
                    </ItemRow>
                  ) : (
                    <ItemRow info>
                      <span>🏫 Lớp đang phụ trách</span>
                      <strong style={{color:'#9ca3af', fontWeight:400}}>Không có</strong>
                    </ItemRow>
                  )}

                  {deletePreview.counts?.lessonDiariesCreated > 0 ? (
                    <ItemRow info>
                      <span>📓 Nhật kí đã tạo/cập nhật (chuyển tác giả → admin)</span>
                      <strong>{deletePreview.counts.lessonDiariesCreated} bản ghi</strong>
                    </ItemRow>
                  ) : (
                    <ItemRow info>
                      <span>📓 Nhật kí đã tạo/cập nhật</span>
                      <strong style={{color:'#9ca3af', fontWeight:400}}>Không có</strong>
                    </ItemRow>
                  )}

                  {deletePreview.counts?.attendanceSessionsCreated > 0 ? (
                    <ItemRow info>
                      <span>✅ Buổi điểm danh đã tạo/cập nhật (chuyển người tạo → admin)</span>
                      <strong>{deletePreview.counts.attendanceSessionsCreated} buổi</strong>
                    </ItemRow>
                  ) : (
                    <ItemRow info>
                      <span>✅ Buổi điểm danh đã tạo/cập nhật</span>
                      <strong style={{color:'#9ca3af', fontWeight:400}}>Không có</strong>
                    </ItemRow>
                  )}
                </>
              )}

              {deletePreview.role === 2 && (
                <ItemRow info>
                  <span>Tài khoản học sinh bị xoá. Hồ sơ học sinh vẫn được giữ lại.</span>
                  <strong>ℹ️</strong>
                </ItemRow>
              )}

              {deletePreview.role === 0 && (
                <ItemRow>
                  <span>Đây là tài khoản Admin. Xoá sẽ mất quyền truy cập.</span>
                  <strong>⚠️</strong>
                </ItemRow>
              )}

              <div style={{ marginTop:14, padding:'12px 14px', borderRadius:10, background:'#eff6ff', border:'1px solid #bfdbfe', fontSize:12, color:'#1e40af', lineHeight:1.6 }}>
                ℹ️ <strong>Dữ liệu KHÔNG bị xoá.</strong> Toàn bộ nhật kí, điểm danh, lớp học vẫn được giữ nguyên — chỉ thay tên giáo viên thành <strong>{userInfo?.userName} (admin)</strong>.
              </div>
            </MBody>
            <MFooter>
              <CancelBtn onClick={handleCancelDelete}>Huỷ bỏ</CancelBtn>
              <DeleteBtn onClick={handleDeleteUser} disabled={deleting}>
                {deleting ? <><Spinner2 /> Đang xoá...</> : <><FiTrash2 /> Xác nhận Xoá user</>}
              </DeleteBtn>
            </MFooter>
          </ModalCard>
        </Overlay>
      )}
    </UsersContainer>
  );
};

export default UserManagePage;
